import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Send, Clock, CheckCircle, AlertCircle, Mail, RefreshCw } from 'lucide-react';

const ADMIN_EMAILS = ['felixmanuela.manda@gmail.com'];

export default function AdminEmails() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [testTemplate, setTestTemplate] = useState(1);

  // Admin check
  const isAdmin = user && ADMIN_EMAILS.includes(user.email);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [isAdmin, navigate]);

  const loadData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statusRes, historyRes] = await Promise.all([
        axios.get(`${API}/admin/weekly-emails/status`, { headers }),
        axios.get(`${API}/admin/weekly-emails/history?limit=5`, { headers })
      ]);
      setStatus(statusRes.data);
      setHistory(historyRes.data.batches || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendNow = async () => {
    if (!window.confirm('Send weekly emails to all eligible users now?')) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/weekly-emails/send-now`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Sent ${res.data.result.sent} emails`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleSendTest = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/admin/weekly-emails/send-test?template_id=${testTemplate}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Test email (template ${testTemplate}) sent to ${user.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { 
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (!isAdmin) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-[#E5C585] animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <div className="border-b border-[#1C1C1C] px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate('/dashboard')}
            className="text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-lg font-medium">Weekly Emails</h1>
            <p className="text-xs text-zinc-500">Admin Panel</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-6 space-y-6">
        
        {/* Status Card */}
        <div className="bg-[#0A0A0A] border border-[#1C1C1C] rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm font-medium">Scheduler Status</span>
            </div>
            <button 
              onClick={loadData}
              className="text-zinc-500 hover:text-white transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-400">Status</span>
              <span className={status?.scheduler_running ? 'text-green-400' : 'text-red-400'}>
                {status?.scheduler_running ? 'Running' : 'Stopped'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Next Send</span>
              <span className="text-white">{formatDate(status?.next_run)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Current Template</span>
              <span className="text-[#E5C585]">Template {status?.current_template_id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Eligible Users</span>
              <span className="text-white">{status?.eligible_users_count || 0}</span>
            </div>
          </div>
        </div>

        {/* Actions Card */}
        <div className="bg-[#0A0A0A] border border-[#1C1C1C] rounded-lg p-5">
          <div className="flex items-center gap-2 mb-4">
            <Send className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-sm font-medium">Actions</span>
          </div>
          
          <div className="space-y-4">
            {/* Send Now */}
            <div>
              <button
                onClick={handleSendNow}
                disabled={sending}
                className="w-full bg-[#D4AF37] text-black py-2.5 px-4 rounded-lg font-medium text-sm hover:bg-[#E5C585] transition-colors disabled:opacity-50"
              >
                {sending ? 'Sending...' : 'Send to All Eligible Users'}
              </button>
              <p className="text-xs text-zinc-500 mt-1.5 text-center">
                Will send template {status?.current_template_id} to {status?.eligible_users_count} users
              </p>
            </div>

            {/* Divider */}
            <div className="border-t border-[#1C1C1C]" />

            {/* Send Test */}
            <div>
              <div className="flex gap-2">
                <select
                  value={testTemplate}
                  onChange={(e) => setTestTemplate(Number(e.target.value))}
                  className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg px-3 py-2 text-sm flex-1"
                >
                  <option value={1}>Template 1</option>
                  <option value={2}>Template 2</option>
                  <option value={3}>Template 3</option>
                </select>
                <button
                  onClick={handleSendTest}
                  disabled={sending}
                  className="bg-[#1C1C1C] text-white py-2 px-4 rounded-lg text-sm hover:bg-[#2A2A2A] transition-colors disabled:opacity-50"
                >
                  Send Test
                </button>
              </div>
              <p className="text-xs text-zinc-500 mt-1.5">
                Test email will be sent to {user?.email}
              </p>
            </div>
          </div>
        </div>

        {/* History Card */}
        <div className="bg-[#0A0A0A] border border-[#1C1C1C] rounded-lg p-5">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-sm font-medium">Recent Batches</span>
          </div>
          
          {history.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-4">No batches sent yet</p>
          ) : (
            <div className="space-y-3">
              {history.map((batch, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-[#1C1C1C] last:border-0">
                  <div>
                    <div className="text-sm">{formatDate(batch.executed_at)}</div>
                    <div className="text-xs text-zinc-500">Template {batch.template_id}</div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-sm">
                      {batch.failed_count === 0 ? (
                        <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                      ) : (
                        <AlertCircle className="w-3.5 h-3.5 text-amber-400" />
                      )}
                      <span>{batch.sent_count}/{batch.total_users}</span>
                    </div>
                    <div className="text-xs text-zinc-500">sent</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Last Batch Details */}
        {status?.last_batch && (
          <div className="text-xs text-zinc-500 text-center">
            Last batch: {formatDate(status.last_batch.executed_at)} • 
            {status.last_batch.sent_count} sent, {status.last_batch.failed_count} failed
          </div>
        )}

      </div>
    </div>
  );
}
